import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { usePermissions } from '../../PermissionsContext';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import './EmployeePopup.css';

const GENDERS = ['Male', 'Female', 'Other'];
const fmtUS = iso => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');

const EmployeePopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    // Ставка — только с правом finance; SSN — только System Admin/Owner.
    // Сервер эти поля без права всё равно не примет и не отдаст.
    const { can } = usePermissions();
    const [form, setForm] = useState({
        employeeName: '', employeeSurname: '', gender: '',
        birthDate: '', socialSecurityNumber: '',
        hireDate: '', rate: '', isActive: 1,
        email: '', phone: '',
    });
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                employeeName:        editData.employeeName        || '',
                employeeSurname:     editData.employeeSurname     || '',
                gender:              editData.gender              || '',
                birthDate:           editData.birthDate           ? (editData.birthDate instanceof Date ? editData.birthDate.toISOString().split('T')[0] : String(editData.birthDate).split('T')[0]) : '',
                socialSecurityNumber:editData.socialSecurityNumber|| '',
                hireDate:            editData.hireDate            ? (editData.hireDate instanceof Date ? editData.hireDate.toISOString().split('T')[0] : String(editData.hireDate).split('T')[0]) : '',
                rate:                editData.rate                || '',
                rate_effective_from: new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
                isActive:            editData.isActive != null ? editData.isActive : 1,
                email:               editData.email               || '',
                phone:               editData.phone               || '',
            });
        } else {
            setForm({ employeeName:'', employeeSurname:'', gender:'',
                      birthDate:'', socialSecurityNumber:'',
                      hireDate:'', rate:'', isActive: 1,
                      email:'', phone:'' });
        }
        setError('');
    }, [isOpen, editData]);

    // Rate history (finance right): GET /api/work-reports/rate-history/:id, shown next to SSN
    const [rateHistory, setRateHistory] = useState(null);
    useEffect(() => {
        if (!isOpen || !editData || !can('finance')) { setRateHistory(null); return; }
        setRateHistory(null);
        api.get(`/api/work-reports/rate-history/${editData.employeeID}`)
            .then(r => setRateHistory(r.data || []))
            .catch(() => setRateHistory([]));
    }, [isOpen, editData]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.employeeName.trim()) { setError('Name is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/employee-popup/${editData.employeeID}`, form);
            } else {
                await api.post(`/api/employee-popup`, form);
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const isActive = Number(form.isActive) === 1;

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup emp-popup">

                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData
                                ? `Edit — ${editData.employeeName}${editData.employeeSurname ? ' ' + editData.employeeSurname : ''}`
                                : 'Add Employee'}
                        </div>
                        <div className="kit-popup__subtitle" style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {editData
                                ? <>
                                    ID #{editData.employeeID}
                                    <span className={`emp-status-badge${isActive ? ' emp-status-badge--active' : ' emp-status-badge--inactive'}`}>
                                        <i className={`fas fa-circle emp-status-dot`} />
                                        {isActive ? 'Active' : 'Inactive'}
                                    </span>
                                  </>
                                : 'Only Name is required — other fields are optional'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
                    </button>
                </div>

                <div className="wm-popup__body">
                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">First Name *</span>
                            <input className="kit-field__input" value={form.employeeName}
                                onChange={setField('employeeName')} placeholder="e.g. Tania"
                                autoFocus={!editData} />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Last Name</span>
                            <input className="kit-field__input" value={form.employeeSurname}
                                onChange={setField('employeeSurname')} placeholder="optional" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Gender</span>
                            <select className="kit-field__input" value={form.gender}
                                onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                                <option value="">— Select —</option>
                                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Date of Birth</span>
                            <input className="kit-field__input" type="date" value={form.birthDate}
                                onChange={setField('birthDate')} />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Hire Date</span>
                            <input className="kit-field__input" type="date" value={form.hireDate}
                                onChange={setField('hireDate')} />
                        </div>

                        {can('finance') && (
                            <div className="kit-field">
                                <span className="kit-field__label">Rate ($/hr)</span>
                                <input className="kit-field__input" type="number" min="0" step="0.01"
                                    value={form.rate} onChange={setField('rate')} placeholder="e.g. 15.00" />
                            </div>
                        )}
                        {can('admin-users') && (
                            <div className="kit-field">
                                <span className="kit-field__label">Social Security #</span>
                                <input className="kit-field__input" value={form.socialSecurityNumber}
                                    onChange={setField('socialSecurityNumber')} placeholder="XXX-XX-XXXX" />
                            </div>
                        )}

                        {can('finance') && editData && (
                            <div className="kit-field">
                                {String(form.rate) !== String(editData.rate ?? '') ? (
                                    <>
                                        <span className="kit-field__label">New rate effective from</span>
                                        <input className="kit-field__input" type="date" value={form.rate_effective_from}
                                            onChange={setField('rate_effective_from')} />
                                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Days before this date keep the previous rate in Daily Reports</div>
                                    </>
                                ) : (
                                    <>
                                        <span className="kit-field__label">Rate history</span>
                                        <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6, paddingTop: 4 }}>
                                            {rateHistory === null && <span style={{ color: '#94A3B8' }}>Loading…</span>}
                                            {rateHistory && !rateHistory.length && <span style={{ color: '#94A3B8' }}>No changes yet — current rate applies to all days</span>}
                                            {rateHistory && rateHistory.map(h => (
                                                <div key={h.id}>
                                                    <b>${Number(h.rate).toFixed(2)}/h</b>{' '}
                                                    {h.effective_from === '1900-01-01'
                                                        ? <span style={{ color: '#94A3B8' }}>before</span>
                                                        : <>from {fmtUS(h.effective_from)}{h.created_by_name ? <span style={{ color: '#94A3B8' }}> · {h.created_by_name}</span> : null}</>}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Contact info */}
                        <div className="kit-field emp-section-header">
                            <span className="emp-section-title">
                                <i className="fas fa-address-card" /> Contact Information
                                <span className="emp-section-hint"> — optional</span>
                            </span>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">
                                <i className="fas fa-envelope emp-field-icon" /> Email
                            </span>
                            <input className="kit-field__input" type="email"
                                value={form.email} onChange={setField('email')}
                                placeholder="e.g. tania@example.com" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">
                                <i className="fas fa-phone emp-field-icon" /> Phone
                            </span>
                            <input className="kit-field__input" type="tel"
                                value={form.phone} onChange={setField('phone')}
                                placeholder="e.g. +1 (305) 555-0123" />
                        </div>

                        {/* Active toggle — full-width */}
                        <div className="kit-field emp-active-field">
                            <span className="kit-field__label">Status</span>
                            <div className="emp-toggle-row">
                                <button
                                    type="button"
                                    className={`emp-toggle${isActive ? ' emp-toggle--active' : ''}`}
                                    onClick={() => setForm(p => ({ ...p, isActive: isActive ? 0 : 1 }))}
                                >
                                    <span className="emp-toggle__thumb" />
                                </button>
                                <span className={`emp-toggle-label${isActive ? '' : ' emp-toggle-label--off'}`}>
                                    {isActive ? 'Active' : 'Inactive — will not appear in production dropdowns'}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Add Employee')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default EmployeePopup;
